import { Lms } from "@verity/api-client/types";
import i18nPackage from "../i18n/i18n";
import { ExtendedLms, SetupStepId, TextConfig } from "./QuickSetupText";
import { createContext, CSSProperties, useContext } from "react";
import { INTEGRATION_TYPES, IntegrationType, SETUP_GUIDE_ATTRIBUTES } from "./setupGuideConstants";
import { InstituteSetupGuide } from "@yujacorp/verity-api-contract";

type IntegrationConfiguration = {
	type: IntegrationType;
	completed: boolean;
	disabled?: boolean;
};

type IntegrationConfigRecord = Record<IntegrationType, { completed: boolean; visible: boolean; disabled?: boolean }>;

export type IntegrationConfigPayload = {
	array: IntegrationConfiguration[];
	record: IntegrationConfigRecord;
};

export type GetIntegrationConfigurationParams = Partial<
	Pick<
		InstituteSetupGuide,
		| "apiSetupComplete"
		| "visualizerSetupComplete"
		| "lti1p3SetupComplete"
		| "extensionSetupComplete"
		| "profileSetupComplete"
		| "createSetupComplete"
		| "takeSetupComplete"
		| "resultSetupComplete"
	>
> & {
	lms: Lms | undefined;
	// If none of the steps are clickable, pass true here. (for chrome extension when we can't direct them to the LTI)
	disabledTypes?: IntegrationType[];
};

export function getIntegrationConfiguration(params: GetIntegrationConfigurationParams): IntegrationConfigPayload {
	const {
		lms,
		apiSetupComplete = false,
		visualizerSetupComplete = false,
		lti1p3SetupComplete = false,
		extensionSetupComplete = false,
		profileSetupComplete = false,
		createSetupComplete = false,
		takeSetupComplete = false,
		resultSetupComplete = false,
		disabledTypes = [],
	} = params;

	const isDisabled = (type: IntegrationType) => disabledTypes.includes(type);

	const isLms = Boolean(lms);
	const isNotBlackboard = Boolean(isLms && lms !== "BlackboardLearn");

	const record: IntegrationConfigRecord = {
		"LTI 1.3": { completed: lti1p3SetupComplete, visible: isLms, disabled: isDisabled("LTI 1.3") },
		"LMS API": { completed: apiSetupComplete, visible: isNotBlackboard, disabled: isDisabled("LMS API") },
		"LMS Visualizer": {
			completed: visualizerSetupComplete,
			visible: isNotBlackboard,
			disabled: isDisabled("LMS Visualizer"),
		},
		"Create Profile": { completed: profileSetupComplete, visible: true, disabled: isDisabled("Create Profile") },
		"Browser Extension": {
			completed: extensionSetupComplete,
			visible: isLms,
			disabled: isDisabled("Browser Extension"),
		},
		"Create Assessment": {
			completed: createSetupComplete,
			visible: isLms,
			disabled: isDisabled("Create Assessment"),
		},
		"Take Assessment": {
			completed: takeSetupComplete,
			visible: isLms,
			disabled: isDisabled("Take Assessment"),
		},
		"View Results": {
			completed: resultSetupComplete,
			visible: isLms,
			disabled: isDisabled("View Results"),
		},
	};

	const array = INTEGRATION_TYPES.map((type) => ({
		type,
		completed: record[type].completed,
		visible: record[type].visible,
		disabled: record[type].disabled,
	})).filter((item) => item.visible);

	return { array, record };
}

export const SETUP_HIGHLIGHT_STYLE = {
	border: "2px solid #7A61CF",
	borderRadius: "7px",
	boxShadow: "0px 0px 10px 0px #4F34AC40",
	zIndex: SETUP_GUIDE_ATTRIBUTES.Z_INDEX.HIGHLIGHT,
	pointerEvents: "none",
	position: "fixed",
} satisfies CSSProperties;

export type PromptPosition = "top" | "bottom" | "left" | "right";
export type PromptAlignment = "start" | "center" | "end";

export const PROMPT_GAP_SPACING_PX = 12;
const PROMPT_GAP_SPACING = `${PROMPT_GAP_SPACING_PX}px`;

export function getPromptPlacementStyles(position: PromptPosition, align: PromptAlignment = "start"): CSSProperties {
	const styles: CSSProperties = {};

	switch (position) {
		case "top":
			styles.bottom = `calc(100% + ${PROMPT_GAP_SPACING})`;
			break;
		case "bottom":
			styles.top = `calc(100% + ${PROMPT_GAP_SPACING})`;
			break;
		case "left":
			styles.right = `calc(100% + ${PROMPT_GAP_SPACING})`;
			break;
		case "right":
		default:
			styles.left = `calc(100% + ${PROMPT_GAP_SPACING})`;
			break;
	}

	const isVerticalSide = position === "top" || position === "bottom";

	if (isVerticalSide) {
		// If tooltip is above/below, align horizontally (Left / Right bounds)
		if (align === "start") {
			styles.left = "0px";
		} else if (align === "end") {
			styles.right = "0px"; // Align with the right edge of the button
		} else if (align === "center") {
			styles.left = "50%";
			styles.transform = "translateX(-50%)"; // center alignment
		}
	} else {
		// If tooltip is left/right side, align vertically (Top / Bottom bounds)
		if (align === "start") {
			styles.top = "0px";
		} else if (align === "end") {
			styles.bottom = "0px"; // Align with the bottom edge of the button
		} else if (align === "center") {
			styles.top = "50%";
			styles.transform = "translateY(-50%)"; // center alignment
		}
	}

	return styles;
}
export type TrackerRect = {
	top: number;
	left: number;
	width: number;
	height: number;
};

export type TrackElementPositionOptions = {
	paddingX?: number;
	paddingY?: number;
	/**Shrinks the highlight box's width/height. Does not move the box. */
	shrinkX?: number;
	/**Shrinks the highlight box's width/height. Does not move the box. */
	shrinkY?: number;

	/**Translates the highlight box's top/left without affecting its size. */
	positionOffset?: { top?: number; left?: number };

	/**If in shadow dom (D2L), need to add event listeners to patch scroll
	 * because scroll events in shadow dom do not bubble to the window */
	inShadowDom?: boolean;

	/**Will attach a global mutation observer. This is for moodle where they have accordians and need to update position */
	watchGlobalLayoutMutations?: boolean;
};

export function trackElementPosition(
	element: HTMLElement,
	onPositionChange: (rect: TrackerRect) => void,
	options: TrackElementPositionOptions = {},
) {
	const {
		paddingX = 7,
		paddingY = 8,
		shrinkX = 4,
		shrinkY = 4,
		inShadowDom = false,
		positionOffset = { top: 0, left: 0 },
		watchGlobalLayoutMutations = false,
	} = options;

	let lastTop = 0;
	let lastLeft = 0;
	let lastWidth = 0;
	let lastHeight = 0;
	let frameId: number | null = null;
	/**Need to keep track of the end time for the shadow DOM loop because shadow dom doesn't tell us when the scroll stops.
	 * This is for when the user drags the scrollbar. need to keep updating the position until they stop dragging.
	 */
	let loopEndTime = 0;

	const updatePosition = () => {
		if (!element || !element.isConnected) return;
		const rect = element.getBoundingClientRect();

		if (rect.top !== lastTop || rect.left !== lastLeft || rect.width !== lastWidth || rect.height !== lastHeight) {
			lastTop = rect.top;
			lastLeft = rect.left;
			lastWidth = rect.width;
			lastHeight = rect.height;

			onPositionChange({
				top: rect.top - paddingY + (positionOffset.top ?? 0),
				left: rect.left - paddingX + (positionOffset.left ?? 0),
				width: rect.width + (paddingX * 2 - shrinkX),
				height: rect.height + (paddingY * 2 - shrinkY),
			});
		}
	};

	const runShadowDomLoop = () => {
		updatePosition();
		if (performance.now() < loopEndTime) {
			frameId = requestAnimationFrame(runShadowDomLoop);
		} else {
			frameId = null;
		}
	};
	const wakeUpShadowLoop = (duration = 500) => {
		loopEndTime = performance.now() + duration;
		if (frameId === null) {
			frameId = requestAnimationFrame(runShadowDomLoop);
		}
	};
	const triggerShadowTracking = () => wakeUpShadowLoop(500);

	const triggerNormalTracking = () => {
		if (frameId !== null) return;
		frameId = requestAnimationFrame(() => {
			frameId = null;
			updatePosition();
		});
	};

	const triggerTracking = inShadowDom ? triggerShadowTracking : triggerNormalTracking;

	updatePosition();

	const resizeObserver = new ResizeObserver(() => {
		updatePosition();
	});
	resizeObserver.observe(element);

	let globalMutationObserver: MutationObserver | null = null;

	if (watchGlobalLayoutMutations) {
		globalMutationObserver = new MutationObserver(() => {
			triggerTracking();
		});

		globalMutationObserver.observe(document.body, {
			attributes: true,
			childList: true,
			subtree: true,
			attributeFilter: ["class", "style"],
		});
	}

	if (inShadowDom) {
		// If there is css animation when element opens
		wakeUpShadowLoop(600);

		// This is for when you use your wheel to scroll
		window.addEventListener("wheel", triggerTracking, { passive: true });

		// Need this so if you drag the scrollbar, the highlight will update
		window.addEventListener("pointermove", triggerTracking, { passive: true });

		// Need this, otherwise when you use arrow keys to scroll, the highlight does not update
		window.addEventListener("keydown", triggerTracking, { capture: true, passive: true });
		window.addEventListener("focusin", triggerTracking, { capture: true, passive: true });
	} else {
		window.addEventListener("scroll", triggerTracking, { capture: true, passive: true });
	}

	window.addEventListener("resize", triggerTracking, { passive: true });

	// Cleanup function
	return () => {
		if (frameId !== null) cancelAnimationFrame(frameId);
		resizeObserver.disconnect();
		globalMutationObserver?.disconnect();
		window.removeEventListener("resize", triggerTracking);

		if (inShadowDom) {
			window.removeEventListener("wheel", triggerTracking);
			window.removeEventListener("pointermove", triggerTracking);
			window.removeEventListener("keydown", triggerTracking, { capture: true });
			window.removeEventListener("focusin", triggerTracking, { capture: true });
		} else {
			window.removeEventListener("scroll", triggerTracking, { capture: true });
		}
	};
}

/**
 * Whether a Create/Take/View click should skip the setup guide's earlier steps
 * because the user is jumping straight into the LMS via a direct LTI/content
 * link (e.g. D2L's "Take Assessment" always launches straight into the quiz,
 * bypassing the LMS quiz-list navigation steps).
 *
 */
export function checkSetupGuideFastTrack(
	lms: Lms,
	type: IntegrationType,
	isBlackboardUltra: boolean = true,
	createQuizUrl?: string | null,
): boolean {
	if (lms === "desire2learn") {
		return type === "Take Assessment";
	}

	if (lms === "moodle") {
		return type === "View Results";
	}

	if (lms === "BlackboardLearn") {
		if (type === "Create Assessment" && createQuizUrl) {
			return createQuizUrl.includes("content_id");
		}

		if (type === "Take Assessment") {
			return !isBlackboardUltra;
		}

		return type === "View Results";
	}

	return false;
}

export function getExtendedLmsFromLms(lms: Lms, isBlackboardUltra = true, moodleVers?: "3.X" | "4.X"): ExtendedLms {
	if (lms === "moodle") {
		if (moodleVers === "3.X") {
			return "moodle3p9";
		}
		return "moodle4p1";
	} else if (lms === "BlackboardLearn") {
		return isBlackboardUltra ? "blackboardUltra" : "blackboardOriginal";
	}
	return lms;
}

export type GetSetupStepDetailsOptions = {
	skipStepsBeforeId?: SetupStepId;
};

export function getSetupStepDetails(
	workflowSteps: TextConfig[],
	targetId: SetupStepId,
	options?: GetSetupStepDetailsOptions,
): { title: React.ReactNode; description: React.ReactNode; chipText: string; index: number } | null {
	let stepsToCount = workflowSteps;
	if (options?.skipStepsBeforeId) {
		const cutIndex = workflowSteps.findIndex((step) => step.id === options.skipStepsBeforeId);
		if (cutIndex !== -1) {
			stepsToCount = workflowSteps.slice(cutIndex);
		}
	}

	const index = stepsToCount.findIndex((step) => step.id === targetId);

	if (index === -1) {
		console.warn(`[Verity] Step ID "${targetId}" not found in the provided workflow layout array.`);
		return null;
	}

	const config = stepsToCount[index];
	const totalSteps = stepsToCount.length;

	const chipText = i18nPackage.t("quickSetupGuide.stepProgress", {
		current: index + 1,
		total: totalSteps,
	});

	return {
		title: config.title,
		description: config.description,
		chipText,
		index,
	};
}

export const SetupGuideContext = createContext<{
	steps: TextConfig[];
}>({ steps: [] });
export const useSetupGuideContext = () => useContext(SetupGuideContext);
